/**
 * O que a caixa "Lançado no Financeiro" mostra depois que um pedido vira
 * comprado, e quando ela aparece.
 *
 * Papel: aqui ficam `lancaNaVirada`, que diz se a gravação de um pedido gera
 * despesa no caixa, e `despesasAvisadas`, que monta as linhas da caixa a partir
 * de `despesasDoPedido` — a mesma função com que `compras-atualizar` e
 * `compras-criar` gravam as despesas, e por isso a mesma data, descrição e
 * classificação que vão para Finanças.
 *
 * Como usar: quem grava a passagem para `comprado` consulta `lancaNaVirada` com
 * o pedido como estava antes, e só com a resposta sim chama `despesasAvisadas`
 * com o que foi gravado. Montar a caixa sem a consulta anunciaria despesa nova
 * para o pedido que volta a `comprado` mantendo a de antes, que a API não lança
 * de novo.
 *
 * A caixa não espera a API dizer o que gravou: a gravação devolve o pedido, e
 * não as despesas. O item sem categoria nunca chega aqui, porque a caixa de
 * compra, o "Salvar" e a API recusam a virada antes.
 *
 * O formulário grava e volta ao quadro, e a caixa abre lá: as despesas viajam
 * no estado da navegação, escritas por `estadoComDespesas` e lidas por
 * `despesasDoEstado`. Os dois lados passam por aqui para que a chave e o
 * formato não se desencontrem.
 */
import {
  type ItemQueViraDespesa,
  type StatusPedidoCompra,
  despesasDoPedido,
  valorFinalDoItem,
  viraComprado,
} from '@concierge/shared';
import { z } from 'zod';

/** Uma linha da caixa "Lançado no Financeiro": uma despesa que foi para o caixa. */
export interface DespesaAvisada {
  /** O número do pedido que gerou a despesa. */
  numero: string;
  /** A data da despesa, em ISO 8601. */
  data: string;
  descricao: string;
  /** O fornecedor do item, ou `null` quando ele está em branco. */
  favorecido: string | null;
  categoria: string;
  subcategoria: string | null;
  /** O valor final do item, com desconto e acréscimo. */
  valor: number;
}

/**
 * Diz se gravar o pedido na etapa `atual` lança despesas no caixa.
 *
 * Quem chama é a tela que grava a passagem para `comprado` — o quadro, pela
 * caixa de compra, e o formulário, pelo "Salvar" —, antes de mostrar a caixa
 * "Lançado no Financeiro".
 *
 * @param anterior - O pedido como estava gravado, ou `null` no pedido que está
 *   sendo criado. `lancadoNoFinanceiro` verdadeiro é o pedido que voltou a
 *   `comprado` mantendo as despesas, e a API não lança de novo.
 * @param atual - A etapa com que o pedido vai ser gravado.
 * @returns Verdadeiro só na chegada a `comprado` de um pedido sem despesa.
 */
export function lancaNaVirada(
  anterior: { status: StatusPedidoCompra; lancadoNoFinanceiro: boolean } | null,
  atual: StatusPedidoCompra,
): boolean {
  return (
    viraComprado(anterior?.status ?? null, atual) && !(anterior?.lancadoNoFinanceiro ?? false)
  );
}

/**
 * As despesas que a virada para `comprado` gravou, como a caixa as mostra.
 *
 * Uma linha por despesa: uma por item à vista, e uma por parcela no item
 * parcelado, cada parcela com a própria data e o " (i/N)" na descrição, como
 * Finanças as recebe.
 *
 * @param pedido - O pedido como foi gravado: o número, a data e os itens.
 * @returns As linhas, na ordem dos itens e das parcelas.
 */
export function despesasAvisadas(pedido: {
  numero: string;
  data: string;
  itens: readonly ItemQueViraDespesa[];
}): DespesaAvisada[] {
  return despesasDoPedido(pedido, pedido.itens).flatMap((despesa) => {
    const item = pedido.itens[despesa.itemPosicao];
    if (item === undefined) return [];
    return [
      {
        numero: pedido.numero,
        data: despesa.data,
        descricao: despesa.descricao,
        favorecido: despesa.fornecedor,
        categoria: despesa.categoria,
        subcategoria: despesa.subcategoria,
        valor: valorFinalDoItem(item),
      },
    ];
  });
}

/** O estado da navegação que leva as despesas do formulário ao quadro. */
const estadoComDespesasSchema = z.object({
  despesasLancadas: z.array(
    z.object({
      numero: z.string(),
      data: z.string(),
      descricao: z.string(),
      favorecido: z.string().nullable(),
      categoria: z.string(),
      subcategoria: z.string().nullable(),
      valor: z.number(),
    }),
  ),
});

/**
 * O estado da navegação com que o formulário volta ao quadro depois de lançar.
 *
 * @param despesas - As linhas de `despesasAvisadas`.
 * @returns O objeto a passar em `navigate('/compras', { state })`, que
 *   `despesasDoEstado` lê do outro lado.
 */
export function estadoComDespesas(despesas: readonly DespesaAvisada[]): {
  despesasLancadas: DespesaAvisada[];
} {
  return { despesasLancadas: [...despesas] };
}

/**
 * As despesas que o formulário deixou no estado da navegação.
 *
 * @param estado - O `location.state`, que pode ser qualquer coisa: nulo na
 *   entrada comum, ou o que outra tela tenha posto ali.
 * @returns As despesas, ou a lista vazia quando o estado não as traz.
 */
export function despesasDoEstado(estado: unknown): DespesaAvisada[] {
  const lido = estadoComDespesasSchema.safeParse(estado);
  return lido.success ? lido.data.despesasLancadas : [];
}
